import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarCheck,
  CalendarPlus,
  ChevronRight,
  CreditCard,
  Gift,
  LayoutDashboard,
  LogIn,
  LogOut,
  MapPin,
  Menu,
  RefreshCw,
  Repeat,
  Ticket,
  User,
  WalletCards,
} from 'lucide-react';

import type { HeaderProps } from './types';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { VisuallyHidden } from '@/components/ui/visually-hidden';

import { cn } from '@/lib/utils';

const isActivePath = (href: string | string[]): boolean => {
  const hrefs = Array.isArray(href) ? href : [href];
  const currentPath = window.location.pathname;

  return hrefs.some((h) => {
    if (!h) {
      return false;
    }

    const linkPath = new URL(h, window.location.origin).pathname;

    // Exact match for root path
    if (linkPath === '/') {
      return currentPath === '/';
    }

    return currentPath.startsWith(linkPath);
  });
};

type NavLinkProps = {
  href: string;
  activePaths?: string[];
  children: ReactNode;
};

const NavLink = (props: NavLinkProps) => {
  const { href, activePaths, children } = props;
  const isActive = isActivePath(activePaths || href);

  return (
    <a
      href={href}
      className={cn(
        'relative px-4 py-2 text-sm font-medium transition-all duration-300',
        'after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:-translate-x-1/2',
        'after:bg-primary after:transition-all after:duration-300',
        isActive
          ? 'text-primary-soft-foreground after:w-full'
          : 'text-muted-foreground hover:text-foreground after:w-0 hover:after:w-full',
      )}
    >
      {children}
    </a>
  );
};

type MobileNavItemProps = {
  href: string;
  activePaths?: string[];
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
};

const MobileNavItem = (props: MobileNavItemProps) => {
  const { href, activePaths, icon, children, onClick } = props;
  const isActive = isActivePath(activePaths || href);

  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        'group flex items-center gap-4 rounded-xl px-4 py-3.5',
        'transition-all duration-300',
        isActive
          ? 'bg-primary text-primary-foreground shadow-primary/25 shadow-lg'
          : 'text-foreground hover:bg-muted',
      )}
    >
      <span
        className={cn(
          'flex size-10 items-center justify-center rounded-lg',
          'transition-all duration-300',
          isActive
            ? 'bg-primary-foreground/20'
            : 'bg-primary/10 text-primary-soft-foreground group-hover:bg-primary group-hover:text-primary-foreground',
        )}
      >
        {icon}
      </span>
      <span className="flex-1 font-medium">{children}</span>
      <ChevronRight
        className={cn(
          'size-4 transition-transform duration-300',
          'group-hover:translate-x-1',
          isActive ? 'text-primary-foreground/70' : 'text-muted-foreground',
        )}
      />
    </a>
  );
};

export const Header = (props: HeaderProps) => {
  const { navigation, clientData, payment_maintenance_warning } = props;
  const { t } = useTranslation();

  const {
    logo_url,
    home_page_url,
    my_visits_url,
    dashboard_url,
    auth_url,
    my_booking_url,
    booking_types_url,
    booking_class_url,
    booking_spot_event_url,
    booking_url,
    online_booking_url,
    my_reservations_url,
    my_subscription_url,
    tickets_url,
    cards_url,
    subscription_cards_url,
    client_subscription_url,
    certificates_url,
    client_certificates_url,
    subscriptions_url,
    online_certificates_url,
  } = navigation;

  const bookingActivePaths = [
    booking_types_url,
    booking_class_url,
    booking_spot_event_url,
    booking_url,
  ].filter((path): path is string => Boolean(path));

  const isBookingAvailable = !payment_maintenance_warning;

  const { client_name, log_out_props } = clientData || {};

  const [isMenuOpened, setIsMenuOpened] = useState(false);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpened) {
        setIsMenuOpened(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpened]);

  const handleCloseMenu = () => {
    setIsMenuOpened(false);
  };

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 w-full',
          'border-border/40 bg-background/80 border-b backdrop-blur-xl',
          'supports-backdrop-filter:bg-background/60',
        )}
      >
        <div className="mx-auto flex h-16 items-center justify-between px-4 lg:h-20 lg:px-8">
          {/* Logo */}
          <a
            href={home_page_url}
            className="group flex items-center transition-transform duration-300 hover:scale-105"
          >
            <img
              className="h-10 w-auto lg:h-12"
              src={logo_url}
              alt="logo"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {isBookingAvailable && booking_types_url && (
              <NavLink
                href={booking_types_url}
                activePaths={bookingActivePaths}
              >
                {t('book')}
              </NavLink>
            )}
            {isBookingAvailable && online_booking_url && (
              <NavLink href={online_booking_url}>{t('bookOnline')}</NavLink>
            )}
            {online_certificates_url && (
              <NavLink href={online_certificates_url}>
                {t('certificates')}
              </NavLink>
            )}
            {certificates_url && (
              <NavLink href={certificates_url}>
                {t('certificatesCatalog')}
              </NavLink>
            )}
            {!!client_name && (
              <>
                {tickets_url && (
                  <NavLink href={tickets_url}>{t('tickets')}</NavLink>
                )}
                {cards_url && <NavLink href={cards_url}>{t('cards')}</NavLink>}
                {subscription_cards_url && (
                  <NavLink href={subscription_cards_url}>
                    {t('subscriptionCards')}
                  </NavLink>
                )}
                {subscriptions_url && (
                  <NavLink href={subscriptions_url}>
                    {t('subscriptions')}
                  </NavLink>
                )}
              </>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden items-center gap-3 md:flex">
            {!client_name ? (
              <Button
                asChild
                className="gap-2"
              >
                <a href={auth_url}>
                  <LogIn className="size-4" />
                  {t('authorize')}
                </a>
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      'group gap-2 rounded-full px-3',
                      'hover:bg-primary/10 hover:text-primary-soft-foreground',
                      'focus-visible:ring-0 focus-visible:ring-offset-0',
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full',
                        'bg-primary/10 text-primary-soft-foreground',
                        'group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300',
                      )}
                    >
                      <User className="size-4" />
                    </span>
                    <span className="max-w-30 truncate text-sm font-medium">
                      {client_name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 rounded-xl p-2"
                >
                  <div className="mb-2 px-2 py-1.5">
                    <p className="text-muted-foreground text-xs">
                      {t('welcome', { name: '' })}
                    </p>
                    <p className="font-medium">{client_name}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup className="space-y-1">
                    {my_visits_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={my_visits_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <MapPin className="text-primary-soft-foreground size-4" />
                          {t('myVisits')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {dashboard_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={dashboard_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <LayoutDashboard className="text-primary-soft-foreground size-4" />
                          {t('myDashboard')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {my_booking_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={my_booking_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <CalendarCheck className="text-primary-soft-foreground size-4" />
                          {t('myBookings')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {my_reservations_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={my_reservations_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <CalendarCheck className="text-primary-soft-foreground size-4" />
                          {t('myBookingsOnline')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {my_subscription_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={my_subscription_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <CreditCard className="text-primary-soft-foreground size-4" />
                          {t('mySubscription')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {client_subscription_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={client_subscription_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <RefreshCw className="text-primary-soft-foreground size-4" />
                          {t('myCardSubscription')}
                        </a>
                      </DropdownMenuItem>
                    )}
                    {client_certificates_url && (
                      <DropdownMenuItem
                        asChild
                        className="rounded-lg"
                      >
                        <a
                          href={client_certificates_url}
                          className="flex cursor-pointer items-center gap-3"
                        >
                          <Gift className="text-primary-soft-foreground size-4" />
                          {t('myCertificates')}
                        </a>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <form
                    method="post"
                    action={log_out_props?.url}
                  >
                    <input
                      type="hidden"
                      name="_method"
                      value="delete"
                    />
                    <input
                      type="hidden"
                      name="authenticity_token"
                      value={log_out_props?.token || ''}
                    />
                    <DropdownMenuItem
                      asChild
                      className="rounded-lg"
                    >
                      <button
                        type="submit"
                        className="text-destructive-soft-foreground focus:text-destructive-soft-foreground flex w-full cursor-pointer items-center gap-3"
                      >
                        <LogOut className="size-4" />
                        {t('logOut')}
                      </button>
                    </DropdownMenuItem>
                  </form>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'relative size-10 rounded-full md:hidden',
              'hover:bg-primary/10 hover:text-primary-soft-foreground',
            )}
            onClick={() => setIsMenuOpened(true)}
            aria-label="Open menu"
          >
            <Menu className="size-5" />
          </Button>
        </div>
      </header>

      {/* Mobile Menu */}
      <Sheet
        open={isMenuOpened}
        onOpenChange={setIsMenuOpened}
      >
        <SheetContent
          side="right"
          aria-describedby={undefined}
          className={cn(
            'w-full max-w-sm border-l-0 p-0',
            'from-background via-background to-primary/5 bg-linear-to-br',
          )}
        >
          <VisuallyHidden>
            <SheetHeader>
              <SheetTitle>Navigation Menu</SheetTitle>
            </SheetHeader>
          </VisuallyHidden>

          {/* Mobile Header */}
          {client_name && (
            <div className="border-border/40 flex items-center gap-3 border-b px-6 py-4">
              <div
                className={cn(
                  'flex size-9 items-center justify-center rounded-full',
                  'bg-primary text-primary-foreground',
                )}
              >
                <User className="size-4" />
              </div>
              <span className="truncate text-sm font-medium">
                {client_name}
              </span>
            </div>
          )}

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            {/* Main Actions */}
            <div className="space-y-2">
              {isBookingAvailable && booking_types_url && (
                <MobileNavItem
                  href={booking_types_url}
                  activePaths={bookingActivePaths}
                  icon={<CalendarPlus className="size-5" />}
                  onClick={handleCloseMenu}
                >
                  {t('book')}
                </MobileNavItem>
              )}

              {isBookingAvailable && online_booking_url && (
                <MobileNavItem
                  href={online_booking_url}
                  icon={<CalendarPlus className="size-5" />}
                  onClick={handleCloseMenu}
                >
                  {t('bookOnline')}
                </MobileNavItem>
              )}

              {online_certificates_url && (
                <MobileNavItem
                  href={online_certificates_url}
                  icon={<Gift className="size-5" />}
                  onClick={handleCloseMenu}
                >
                  {t('certificates')}
                </MobileNavItem>
              )}

              {certificates_url && (
                <MobileNavItem
                  href={certificates_url}
                  icon={<Gift className="size-5" />}
                  onClick={handleCloseMenu}
                >
                  {t('certificatesCatalog')}
                </MobileNavItem>
              )}

              {!!client_name && (
                <>
                  {tickets_url && (
                    <MobileNavItem
                      href={tickets_url}
                      icon={<Ticket className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('tickets')}
                    </MobileNavItem>
                  )}

                  {cards_url && (
                    <MobileNavItem
                      href={cards_url}
                      icon={<WalletCards className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('cards')}
                    </MobileNavItem>
                  )}

                  {subscription_cards_url && (
                    <MobileNavItem
                      href={subscription_cards_url}
                      icon={<Repeat className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('subscriptionCards')}
                    </MobileNavItem>
                  )}

                  {subscriptions_url && (
                    <MobileNavItem
                      href={subscriptions_url}
                      icon={<Repeat className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('subscriptions')}
                    </MobileNavItem>
                  )}
                </>
              )}
            </div>

            {/* User Menu */}
            {!!client_name && (
              <>
                <Separator className="my-6" />
                <p className="text-muted-foreground mb-3 px-4 text-xs font-semibold tracking-widest uppercase">
                  {t('account')}
                </p>
                <div className="space-y-2">
                  {my_visits_url && (
                    <MobileNavItem
                      href={my_visits_url}
                      icon={<MapPin className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myVisits')}
                    </MobileNavItem>
                  )}

                  {dashboard_url && (
                    <MobileNavItem
                      href={dashboard_url}
                      icon={<LayoutDashboard className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myDashboard')}
                    </MobileNavItem>
                  )}

                  {my_booking_url && (
                    <MobileNavItem
                      href={my_booking_url}
                      icon={<CalendarCheck className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myBookings')}
                    </MobileNavItem>
                  )}

                  {my_reservations_url && (
                    <MobileNavItem
                      href={my_reservations_url}
                      icon={<CalendarCheck className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myBookingsOnline')}
                    </MobileNavItem>
                  )}

                  {my_subscription_url && (
                    <MobileNavItem
                      href={my_subscription_url}
                      icon={<CreditCard className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('mySubscription')}
                    </MobileNavItem>
                  )}

                  {client_subscription_url && (
                    <MobileNavItem
                      href={client_subscription_url}
                      icon={<RefreshCw className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myCardSubscription')}
                    </MobileNavItem>
                  )}

                  {client_certificates_url && (
                    <MobileNavItem
                      href={client_certificates_url}
                      icon={<Gift className="size-5" />}
                      onClick={handleCloseMenu}
                    >
                      {t('myCertificates')}
                    </MobileNavItem>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer Actions */}
          <div className="border-border/40 border-t p-4">
            {!client_name ? (
              <Button
                asChild
                className="w-full gap-2"
                size="lg"
              >
                <a
                  href={auth_url}
                  onClick={handleCloseMenu}
                >
                  <LogIn className="size-5" />
                  {t('authorize')}
                </a>
              </Button>
            ) : (
              <form
                method="post"
                action={log_out_props?.url}
              >
                <input
                  type="hidden"
                  name="_method"
                  value="delete"
                />
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={log_out_props?.token || ''}
                />
                <button
                  type="submit"
                  className={cn(
                    'group flex w-full cursor-pointer items-center gap-4 rounded-xl px-4 py-3.5',
                    'text-destructive-soft-foreground transition-all duration-300',
                    'hover:bg-destructive hover:text-white',
                  )}
                >
                  <span
                    className={cn(
                      'flex size-10 items-center justify-center rounded-lg',
                      'bg-destructive/10 transition-all duration-300',
                      'group-hover:bg-white/20',
                    )}
                  >
                    <LogOut className="size-5" />
                  </span>
                  <span className="flex-1 text-left font-medium">
                    {t('logOut')}
                  </span>
                  <ChevronRight
                    className={cn(
                      'size-4 opacity-50 transition-all duration-300',
                      'group-hover:translate-x-1 group-hover:opacity-70',
                    )}
                  />
                </button>
              </form>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
